import type { AddressFile, NotNestedValues } from '@provision/entity-types';
import { ApolloError, gql, useLazyQuery, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { ADDRESS_FILE_FIELDS } from 'src/graphql/entities/address-file/address-file.fragments';
import { GET_ADDRESS_FILE_QUERY } from 'src/graphql/entities/address-file/address-file.operations';
import type { MoveAttachmentToSiteFilesAttachment } from 'src/graphql/entities/files-upload.types';
import { useNotifications } from 'src/utils/hooks/use-notifications';

const GET_UPLOAD_INFO_QUERY = gql`
  query GetUploadFileInfo($contentType: String!, $fileKey: String, $hasAttachments: Boolean) {
    getUploadFileInfo(
      input: { contentType: $contentType, fileKey: $fileKey, hasAttachments: $hasAttachments }
    ) {
      putURL
      getURL
    }
  }
`;

interface GetUploadFileInfoQueryResult {
  getUploadFileInfo: {
    putURL: string;
    getURL: string;
  };
}

const GET_PRESIGNED_DELETE_URL_QUERY = gql`
  query GetPreSignedDeleteUrl($fileKey: String!) {
    getPreSignedDeleteUrl(params: { fileKey: $fileKey }) {
      url
    }
  }
`;

interface GetPreSignedDeleteUrlQueryResult {
  getPreSignedDeleteUrl: {
    url: string;
  };
}

const MOVE_ATTACHMENT_TO_SITE_FILES_MUTATION = gql`
  mutation MoveAttachmentToSiteFiles($params: MoveAttachmentToSiteFilesInput!) {
    moveAttachmentToSiteFiles(params: $params) {
      addressFile {
        ...AddressFileFields
      }
    }
  }
  ${ADDRESS_FILE_FIELDS}
`;

interface MoveAttachmentToSiteFilesMutationResult {
  moveAttachmentToSiteFiles: {
    addressFile?: NotNestedValues<AddressFile> & { __typename: 'AddressFile' };
    error?: string;
  };
}

// Unified error handling for all file upload actions
const FILE_UPLOAD_ERRORS = [
  {
    error: 'File key is required',
    message: 'Error: Could not process the request',
  },
  {
    error: 'Attachment not found or invalid attachment ID',
    message: 'Error: Could not process the request',
  },
  {
    error: 'No associated address found for the conversation',
    message: 'Error: Conversation has no associated site',
  },
  {
    error: 'This file is already saved to the address',
    message: 'Error: File already saved to the site',
  },
  {
    error: 'Target address is not associated with the conversation',
    message: 'Error: Site is not associated with this conversation',
  },
];

export const DEFAULT_ERROR = 'Something went wrong. Please try again later.';

export function parseFileUploadError(error: ApolloError): string {
  const errorMessage = FILE_UPLOAD_ERRORS.find((err) =>
    error.graphQLErrors.some((e) => e.message.includes(err.error)),
  )?.message;

  return errorMessage || DEFAULT_ERROR;
}

export const useFileUploadMethods = () => {
  const [getUploadFileInfo, { ...rest }] = useLazyQuery<GetUploadFileInfoQueryResult>(
    GET_UPLOAD_INFO_QUERY,
    { fetchPolicy: 'no-cache', context: { ttl: 0, queryDeduplication: false } },
  );

  return { getUploadFileInfo, ...rest };
};

export const uploadFileToUrl = async (
  file: File,
  putUrl: string,
  options?: { onSuccess: () => void; onError: (error: unknown) => void },
) => {
  const { onSuccess, onError } = options ?? {};

  try {
    const response = await fetch(putUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    onSuccess?.();

    return response.body;
  } catch (error) {
    onError?.(error);
    return null;
  }
};

export const useFileUpload = () => {
  const { getUploadFileInfo } = useFileUploadMethods();

  const uploadFile = useCallback(
    async ({
      file,
      fileKey,
      hasAttachments,
    }: {
      file: File;
      fileKey?: string;
      hasAttachments?: boolean;
    }) => {
      const { data = {} as GetUploadFileInfoQueryResult } = await getUploadFileInfo({
        variables: { contentType: file.type, fileKey, hasAttachments },
      });

      const { putURL, getURL } = data.getUploadFileInfo ?? {};

      if (putURL) {
        await uploadFileToUrl(file, putURL);

        return getURL;
      }

      return null;
    },
    [getUploadFileInfo],
  );

  return { uploadFile };
};

export const useDeleteFile = () => {
  const [getPreSignedDeleteUrl] = useLazyQuery<GetPreSignedDeleteUrlQueryResult>(
    GET_PRESIGNED_DELETE_URL_QUERY,
    { fetchPolicy: 'no-cache', context: { ttl: 0 } },
  );

  const deleteFile = useCallback(
    async (fileKey?: string, onSuccess?: () => void) => {
      if (!fileKey) {
        return;
      }

      const { data } = await getPreSignedDeleteUrl({ variables: { fileKey } });

      const deleteUrl = data?.getPreSignedDeleteUrl?.url;

      if (!deleteUrl) {
        console.error('Failed to get pre-signed delete URL');
        return;
      }

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
      });

      if (!response.ok) {
        console.error(`Delete failed: ${response.statusText}`);
        return;
      }

      onSuccess?.();
    },
    [getPreSignedDeleteUrl],
  );

  return { deleteFile };
};

export const useMoveAttachmentToSiteFiles = () => {
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const [moveToSiteFilesMutation, moveToSiteFilesState] =
    useMutation<MoveAttachmentToSiteFilesMutationResult>(
      MOVE_ATTACHMENT_TO_SITE_FILES_MUTATION,
      {
        onCompleted: () => {
          showSuccessAlert('Saved');
        },
        onError: (error) => {
          showErrorAlert(parseFileUploadError(error));
        },
        refetchQueries: [GET_ADDRESS_FILE_QUERY],
        update(cache) {
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'getAddressFile' });
          cache.gc();
        },
      },
    );

  const moveToSiteFiles = useCallback(
    async ({
      attachment,
      addressId,
      uploadedByUserId,
    }: {
      attachment: MoveAttachmentToSiteFilesAttachment;
      addressId?: string;
      uploadedByUserId?: string;
    }) => {
      if (!attachment?.id) {
        return;
      }

      const optimisticResponse =
        addressId && uploadedByUserId
          ? {
              moveAttachmentToSiteFiles: {
                addressFile: {
                  __typename: 'AddressFile' as const,
                  id: 'temp-address-file-id',
                  addressFileId: 'temp-address-file-id',
                  addressId,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  uploadedBy: uploadedByUserId,
                  pinnedAt: '',
                  contentType: attachment.contentType ?? undefined,
                  fileKey: attachment.fileKey,
                  fileName: attachment.fileName,
                  size: attachment.size ?? undefined,
                  partnerId: attachment.partnerId,
                },
              },
            }
          : undefined;

      await moveToSiteFilesMutation({
        variables: {
          params: {
            attachmentId: attachment.id,
            addressId,
          },
        },
        optimisticResponse,
      });
    },
    [moveToSiteFilesMutation],
  );

  return {
    moveToSiteFiles,
    ...moveToSiteFilesState,
  };
};
